import React, { useState, useEffect } from 'react';
import { Select } from '../../../components/ui/Select';
import { Input } from '../../../components/ui/Input';
import { DownloadIcon, CopyIcon } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ROUTES } from '../../../constants/routes';
import { ArrowLeft20Filled, ArrowLeft24Filled } from '@fluentui/react-icons';
import { Button } from '../../../components/ui/Button';
import { useAuth } from '../../../hooks/useAuth';
import { useRemittanceDetail, useExportLedgerDetail } from '../../../hooks/useRemittance';
import { useDebounce } from '../../../hooks/useDebounce';
import { useDrawer } from '../../../hooks/useDrawer';
import TrackingDetails from '../../../components/TrackingDetails';
import toast from 'react-hot-toast';

const pageSizeOptions = [
  { label: '10 / page', value: '10' },
  { label: '20 / page', value: '20' },
  { label: '50 / page', value: '50' },
  { label: '100 / page', value: '100' },
];

export default function RemittanceDetailsPage() {
  const navigate = useNavigate();
  const { ledgerId } = useParams<{ ledgerId: string }>();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { open: openDrawer, Drawer } = useDrawer();

  const handleTrackingClick = (row: any) => {
    if (!row.trackingNumber) return;
    openDrawer({
      element: (
        <TrackingDetails
          trackingId={row.trackingNumber}
          rtoTrackingId={null}
          orderStatus={row.status || ''}
        />
      ),
      position: 'right',
      className: 'p-4 sm:p-6 md:p-10 w-full max-w-4xl',
      closable: true,
    });
  };

  const [searchTracking, setSearchTracking] = useState('');
  const debouncedSearchTracking = useDebounce(searchTracking, 500);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTracking]);

  const { data, isLoading } = useRemittanceDetail(user?.id, ledgerId, {
    searchTracking: debouncedSearchTracking,
  });

  const { mutate: exportLedgerDetail, isPending: isExporting } = useExportLedgerDetail();

  const handleDownloadDetail = () => {
    if (!user?.id || !ledgerId) {
      toast.error('Unable to export ledger details');
      return;
    }
    exportLedgerDetail({
      userId: user.id,
      ledgerNumber: ledgerId,
      params: {
        searchTracking: debouncedSearchTracking || undefined,
      },
    });
  };

  const responseData = data?.data || data;
  const allRows = Array.isArray(responseData) ? responseData : (responseData?.shipments || []);

  const totalCount = allRows.length;
  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const startIndex = (currentPage - 1) * rowsPerPage;
  const rows = allRows.slice(startIndex, startIndex + rowsPerPage);

  const status = responseData?.status || searchParams.get('status') || 'Unknown';
  const amount = responseData?.totalAmount || searchParams.get('amount') || '0';
  const date = responseData?.date || searchParams.get('date') || '-';

  const handleGoBack = () => {
    navigate(ROUTES.REMITTANCE);
  };

  return (
    <div className="min-h-screen bg-background px-4 lg:px-6 py-4 lg:py-6">
      <div className="mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-2">
          <div>
            <div className="flex flex-row  items-center">
              <Button
                className="bg-background text-text hover:bg-background"
                onClick={handleGoBack}
              >
                <ArrowLeft24Filled className=" cursor-pointer" />
              </Button>
              <h1 className="text-2xl lg:text-3xl font-bold text-text">
                Remittance
              </h1>
            </div>
            <div className="flex flex-col lg:flex-row lg:items-center text-sm text-text3 gap-1 lg:gap-2">
              View and download a detailed bifurcation of your remittance.
            </div>
          </div>
        </div>

        {/* Ledger Card */}
        <div className="bg-[#FFF6EF] border border-[#F6D3B9] rounded-2xl px-4 lg:px-6 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:gap-16 gap-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-text text-lg lg:text-xl">
                  Ledger
                </span>
                <span className="text-sm px-2 py-0.5 rounded-full bg-white text-[#FF9126] font-medium">
                  {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-sm text-text break-all">{ledgerId}</p>
                {ledgerId && (
                  <CopyIcon
                    className="h-4 w-4 text-gray-400 cursor-pointer hover:text-gray-600 transition-colors"
                    onClick={() => {
                      navigator.clipboard.writeText(ledgerId);
                      toast.success('Ledger ID copied');
                    }}
                  />
                )}
              </div>
            </div>

            <div>
              <p className="font-bold text-text text-lg lg:text-xl">Amount</p>
              <p className="text-sm text-text mt-1">{amount}</p>
            </div>

            <div>
              <p className="font-bold text-text text-lg lg:text-xl">Date</p>
              <p className="text-sm text-text mt-1">{date}</p>
            </div>
          </div>
        </div>

        {/* Search + Download */}
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center justify-between">
          <div className="w-full lg:w-96">
            <Input
              placeholder="Search by Tracking Number"
              value={searchTracking}
              onChange={(e) => setSearchTracking(e.target.value)}
            />
          </div>
          <Button
            variant="outline"
            onClick={handleDownloadDetail}
            disabled={isExporting || isLoading}
            className="text-teal hover:text-white font-semibold text-sm h-[42px] flex items-center gap-2"
          >
            <DownloadIcon className="h-4 w-4" />
            {isExporting ? 'Downloading...' : 'Download Excel'}
          </Button>
        </div>

        {/* Table */}
        {/* Table Section */}
        <div className="overflow-x-auto">
          <div className="min-w-[1200px] space-y-4">
            {/* Detached Table Header Card */}
            <div className="bg-teal border rounded-xl px-6 py-3 text-base font-bold text-white shadow-md">
              <div className="grid grid-cols-7 gap-6 text-center">
                <div>Delivery Date</div>
                <div>Tracking Number</div>
                <div>Courier</div>
                <div>Order ID</div>
                <div>Consignee Name</div>
                <div>Zone</div>
                <div>Inv. Value</div>
              </div>
            </div>

            {/* Row Cards */}
            {isLoading ? (
              <div className="bg-white border rounded-xl py-20 text-center text-text3">
                Loading detailed records...
              </div>
            ) : rows.length === 0 ? (
              <div className="bg-white border rounded-xl py-20 text-center text-text3">
                No detailed records found
              </div>
            ) : (
              rows.map((row: any, index: number) => (
                <div
                  key={index}
                  className="bg-card border rounded-xl px-6 py-5 shadow-sm text-center"
                >
                <div className="grid grid-cols-7 gap-6 text-sm text-gray-700 items-start">
                  {/* Delivery Date */}
                  <div className="font-medium text-text">
                    {row.deliveryDate || row.date || '-'}
                  </div>

                  {/* Tracking Number */}
                  <div className="flex items-center justify-center gap-2 group min-h-[20px]">
                    <span
                      onClick={() => handleTrackingClick(row)}
                      className="text-blue-500 underline font-medium cursor-pointer hover:text-blue-700 transition-colors"
                    >
                      {row.trackingNumber || '-'}
                    </span>
                    {row.trackingNumber && (
                      <CopyIcon
                        className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100 cursor-pointer hover:text-gray-600 transition-all"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(row.trackingNumber);
                          toast.success('Tracking Number copied');
                        }}
                      />
                    )}
                  </div>

                  {/* Courier */}
                  <div className="text-gray-600">{row.courier || row.courierName || '-'}</div>

                  {/* Order ID */}
                  <div className="font-medium text-text">{row.orderId || '-'}</div>

                  {/* Consignee Name */}
                  <div className="text-text">{row.consignee || row.consigneeName || '-'}</div>

                  {/* Zone */}
                  <div className="text-gray-600">{row.zone || '-'}</div>

                  {/* Inv. Value */}
                  <div className="font-bold text-text">
                    ₹ {row.invoiceValue ? Number(row.invoiceValue).toLocaleString('en-IN') : 0}
                  </div>
                </div>
              </div>
             )))}

            {!isLoading && (allRows.length > 0 || totalCount > 0) && (
              <div className="mt-6 flex flex-wrap items-center justify-between gap-4 bg-white p-4 rounded-xl border">
                <div className="text-sm text-gray-600 font-medium">
                  Showing page {currentPage}
                  {totalCount > rowsPerPage && totalPages > 0
                    ? ` of ${totalPages}`
                    : ''}
                </div>
                <div className="flex items-center gap-3">
                  <Select
                    options={pageSizeOptions}
                    value={String(rowsPerPage)}
                    position="top"
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  />
                  <Button
                    variant="outline"
                    onClick={() => {
                      setCurrentPage((prev) => Math.max(prev - 1, 1));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    disabled={currentPage <= 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setCurrentPage((prev) =>
                        totalCount > rowsPerPage && totalPages > 0
                          ? Math.min(prev + 1, totalPages)
                          : prev + 1
                      );
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    disabled={
                      totalCount > rowsPerPage && totalPages > 0
                        ? currentPage >= totalPages
                        : rows.length < rowsPerPage
                    }
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}

            {/* Bottom Padding for scroll room */}
            <div className="h-20" />
          </div>
        </div>
        <Drawer />
      </div>
    </div>
  );
}
